/**
 * DynamicAtom - Motion-enabled atom component with real graphics
 *
 * Simple wrapper that delegates drag events to parent for coordinated updates.
 * Handles SVG CTM transformation to convert screen pixels to SVG coordinates.
 *
 * Supports multiple drag behaviors (in priority order):
 * 1. Full translation: When all atoms in molecule are selected, pure translation
 * 2. Anchor orbit: When one atom is selected, others orbit around it
 * 3. Orbit constraint: Single-bond atoms orbit around their neighbor
 * 4. Natural motion: Multi-bond atoms blend translation/rotation based on grab position
 */

import { memo, useRef, useCallback, useEffect, useState } from 'react';
import { useMotion } from '../../contexts/MotionContext';
import { useMolecularSVG } from '../../contexts/MolecularSVGContext';
import { useSelection } from '../../selection';
import { getFormat } from '../../rendering/utils/formatContext';
import { calculateHitboxRadius } from '../../rendering/renderAtomFromRecipe';
import {
  getConstraintType,
  getBondsForAtom,
  applyOrbitConstraint,
  getConnectedAtoms,
  calculateFragmentCentroid,
  calculateBoundingRadius,
  calculateBlendFactor,
  angleFromTo,
  normalizeAngle,
  rotatePoint,
  distance,
  splitFragmentAtAnchor,
  calculateAnchorOrbitAtoms,
  applyAnchorOrbitRotation,
  type Point2D,
  type AnchorOrbitBasis,
} from '../../utils/dragConstraints';
import { DEFAULT_DRAG_SPRING_CONFIG, DEFAULT_NATURAL_MOTION_CONFIG } from '../index';
import { motion, type PanInfo } from 'motion/react';
import type { AtomRenderData } from '../../rendering/useSVGGeneration';
import type { SpringConfig, NaturalMotionConfig, NaturalMotionBasis, AnchorOrbitConfig } from '../types';
import { useFeedbackWithContext } from '../../feedback';

export interface DynamicAtomProps {
  atom: AtomRenderData;
  onDragStart: () => void;
  onDragEnd: (connectedAtoms?: Array<{ id: string; x: number; y: number }>) => void;
  springConfig?: SpringConfig;
  /** Configuration for natural motion (blended translation/rotation) */
  naturalMotionConfig?: NaturalMotionConfig;
  /** Configuration for anchor orbit (selection-based rotation around anchor) */
  anchorOrbitConfig?: AnchorOrbitConfig;
  /**
   * Callback to get anchor data when drag starts.
   * Returns anchor info if anchor orbit should apply, null otherwise.
   * Typically provided by useAnchorOrbit hook.
   */
  getAnchorData?: (draggedAtomId: string) => {
    anchorId: string;
    anchorPosition: Point2D;
  } | null;
  /**
   * Callback to get translation group when drag starts.
   * Returns array of atom IDs if all atoms in molecule are selected (full translation).
   * Typically provided by useAnchorOrbit hook.
   */
  getTranslationGroup?: (draggedAtomId: string) => string[] | null;
  /**
   * Callback when rotation drag starts (optional).
   * Receives the IDs of every atom that will move during this rotation
   * (including the dragged atom itself). Hosts can use this to give
   * visual feedback — e.g. show the rotating atoms as selected so the
   * user can see exactly which atoms are being moved.
   */
  onRotationDragStart?: (orbitingAtomIds?: string[]) => void;
  /**
   * Callback when rotation drag ends (optional)
   */
  onRotationDragEnd?: () => void;
}

export const DynamicAtom = memo(function DynamicAtom({
  atom,
  onDragStart,
  onDragEnd,
  springConfig = DEFAULT_DRAG_SPRING_CONFIG,
  anchorOrbitConfig,
  naturalMotionConfig = DEFAULT_NATURAL_MOTION_CONFIG,
  getAnchorData,
  getTranslationGroup,
  onRotationDragStart,
  onRotationDragEnd,
}: DynamicAtomProps) {
  const motionContext = useMotion();
  const motionValues = motionContext.getAtomMotion(atom.id);
  const { workspace } = useMolecularSVG();
  const gRef = useRef<SVGGElement>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  // Gate for the pan gesture. The GestureManager check runs in
  // handlePointerDown (before the pan threshold); when a trigger claims the
  // gesture we set this true so the onPan* handlers no-op — the same effect
  // the old `dragControls.start()` gate had of never starting a drag.
  const dragPreventedRef = useRef(false);
  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(atom.id);
  const [isDraggingRotation, setIsDraggingRotation] = useState(false);
  // Whether a pan is in progress (drives the drag cursor that `whileDrag`
  // used to provide before we dropped framer-motion's drag feature).
  const [isPanning, setIsPanning] = useState(false);
  const [recentlyEndedDrag, setRecentlyEndedDrag] = useState(false);
  const dragCooldownRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const isSelected = selection?.isSelected(atom.id) ?? false;
  const isHovered = selection?.isHovered(atom.id) ?? false;

  // Check if this atom would trigger rotation mode (anchor orbit) when dragged
  const wouldRotate = useCallback(() => {
    if (!anchorOrbitConfig?.enabled || !getAnchorData) return false;

    // Check if there's an anchor available for this atom
    const anchorData = getAnchorData(atom.id);

    // Rotation applies if there's an anchor (both internal and external anchors)
    return anchorData !== null;
  }, [anchorOrbitConfig, getAnchorData, atom.id]);

  // Determine if we're in rotation mode (for cursor feedback)
  const isRotationMode = wouldRotate();

  // Sync selection state with FeedbackManager (PIPH-73: combined what
  // were previously two separate effects). Both calls touch the same
  // FeedbackManager instance and have no ordering constraint, so they
  // can share an effect — saves one effect run per DynamicAtom per render.
  useEffect(() => {
    feedback.setSelected(isSelected);
    feedback.setHovered(isHovered);
  }, [isSelected, isHovered, feedback]);

  // Override feedback state when in rotation mode and hovered/dragging
  useEffect(() => {
    if (isDraggingRotation) {
      // Actively dragging in rotation mode - set as external state
      if (feedback.context) {
        feedback.context.setState(atom.id, 'anchor-orbit-dragging');
      }
    } else if (isRotationMode && isHovered && !recentlyEndedDrag) {
      // Hovered in rotation mode (not dragging yet) - set as external state
      // Don't show hover state if we just ended a drag (cooldown period)
      if (feedback.context) {
        feedback.context.setState(atom.id, 'anchor-orbit-hover');
      }
    } else {
      // Clear external state when not in rotation mode
      // This allows normal hover/selected states to take over
      if (feedback.context && (isRotationMode !== wouldRotate() || !isHovered)) {
        // Only clear if we were previously in a rotation state
        const currentState = feedback.context.getState(atom.id);
        if (currentState === 'anchor-orbit-hover' || currentState === 'anchor-orbit-dragging') {
          // Set to idle to clear the external state, let compound state take over
          feedback.context.setState(atom.id, 'idle');
        }
      }
    }
  }, [isRotationMode, isHovered, isDraggingRotation, feedback, atom.id, wouldRotate, recentlyEndedDrag]);

  // Get all styling from FeedbackManager
  const opacity = feedback.currentStyle.fillOpacity ?? 0;
  const hitboxColor = feedback.currentStyle.atomColor ?? '#90bcd0';

  // Calculate dynamic hitbox dimensions based on atom display symbol
  const format = getFormat();
  const isSmallScale = atom.atomType === 'carbon'; // Paper carbons are small scale
  const hitboxRadius = calculateHitboxRadius(atom.displaySymbol, format.fontSize, isSmallScale);
  const hitboxWidth = hitboxRadius * 2; // Full width
  const hitboxHeight = format.atom.radius * 2 + 0.2; // Full height (standard atom height)

  // Apply hydrogen offset to hitbox x position to match text centering
  const hydrogenOffset = atom.hydrogenOffset ?? 0;



  // Store constraint metadata (determined on drag start)
  // Extended to support natural motion with NaturalMotionBasis and anchor orbit
  const constraintRef = useRef<
    | {
        type: 'none' | 'orbit';
        anchorX?: number;
        anchorY?: number;
        radius?: number;
      }
    | {
        type: 'natural-motion';
        basis: NaturalMotionBasis;
      }
    | {
        type: 'anchor-orbit';
        basis: AnchorOrbitBasis;
      }
    | {
        type: 'full-translation';
        atomIds: string[];
        initialPositions: Array<{ id: string; x: number; y: number }>;
      }
    | null
  >(null);

  // Stable handler functions to prevent animation leaks
  const handleDragStart = useCallback(() => {
    if (!motionValues) return;

    // Only set if not already captured by first onDrag event
    // Use atom prop values, not MotionValues (which Motion may have already modified)
    if (!dragStartPos.current) {
      dragStartPos.current = { x: atom.x, y: atom.y };
    }

    // FULL TRANSLATION: Check first (highest priority) - all atoms selected = pure translation
    if (anchorOrbitConfig?.enabled && getTranslationGroup) {
      const translationGroup = getTranslationGroup(atom.id);
      if (translationGroup && translationGroup.length > 0) {
        // Capture initial positions for all atoms in the group
        const initialPositions = translationGroup.map((atomId) => {
          const atomMotion = motionContext.getAtomMotion(atomId);
          if (atomMotion) {
            return { id: atomId, x: atomMotion.x.get(), y: atomMotion.y.get() };
          }
          // Fallback to workspace position
          const workspaceAtom = workspace.getAtom(atomId);
          return {
            id: atomId,
            x: workspaceAtom?.x ?? 0,
            y: workspaceAtom ? -workspaceAtom.y : 0, // Y-FLIP
          };
        });

        constraintRef.current = {
          type: 'full-translation',
          atomIds: translationGroup,
          initialPositions,
        };

        onDragStart();
        return; // Skip other constraint detection
      }
    }

    // ANCHOR ORBIT: Check second - selection-based rotation around anchor
    // This takes precedence over natural-motion and orbit constraints
    if (anchorOrbitConfig?.enabled && getAnchorData) {
      const anchorData = getAnchorData(atom.id);
      if (anchorData) {
        // Set rotation dragging state for cursor feedback
        setIsDraggingRotation(true);

        // Check if anchor is in the same molecule as dragged atom
        const draggedMoleculeAtoms = getConnectedAtoms(workspace, atom.id);
        const draggedMoleculeIds = new Set(draggedMoleculeAtoms.map((a) => a.id));
        const isExternalAnchor = !draggedMoleculeIds.has(anchorData.anchorId);

        let orbitingAtoms: Array<{ id: string; x: number; y: number }>;
        let wholeMoleculeOrbit: boolean;

        if (isExternalAnchor) {
          // External anchor (e.g., H-bond acceptor in different molecule)
          // Entire dragged molecule orbits around the external anchor
          orbitingAtoms = draggedMoleculeAtoms;
          wholeMoleculeOrbit = true;
        } else {
          // Internal anchor - split the fragment at the anchor
          const split = splitFragmentAtAnchor(workspace, anchorData.anchorId, atom.id);
          orbitingAtoms = split.dragSideAtoms;
          wholeMoleculeOrbit = split.wholeMoleculeOrbit;
        }

        // Notify parent that rotation drag started, with the IDs of every
        // atom about to move so the host can show them as visually selected.
        onRotationDragStart?.(orbitingAtoms.map((a) => a.id));

        // Use current MotionValue positions instead of workspace positions
        // This prevents offset when MotionValues are mid-animation
        const orbitingAtomsWithMotionPos = orbitingAtoms.map((a) => {
          const atomMotion = motionContext.getAtomMotion(a.id);
          if (atomMotion) {
            return { id: a.id, x: atomMotion.x.get(), y: atomMotion.y.get() };
          }
          return a; // Fallback to workspace position
        });

        // Calculate orbit basis (radius and initial angle for each atom)
        const orbitAtomData = calculateAnchorOrbitAtoms(anchorData.anchorPosition, orbitingAtomsWithMotionPos);

        // Use MotionValue position for dragged atom (where it appears on screen)
        const draggedAtomPos = motionValues
          ? { x: motionValues.x.get(), y: motionValues.y.get() }
          : { x: atom.x, y: atom.y };

        // Initial pointer angle from anchor to dragged atom
        const initialPointerAngle = angleFromTo(anchorData.anchorPosition, draggedAtomPos);

        const basis: AnchorOrbitBasis = {
          anchorId: anchorData.anchorId,
          anchorPosition: anchorData.anchorPosition,
          orbitingAtoms: orbitAtomData,
          wholeMoleculeOrbit,
          initialPointerAngle,
        };

        constraintRef.current = {
          type: 'anchor-orbit',
          basis,
        };

        onDragStart();
        return; // Skip normal constraint detection
      }
    }

    // Determine constraint type and setup.
    // If a skeletalBondFilter is provided, it restricts which bonds count
    // (e.g., only intra-group bonds). The hidden-H filter is always applied.
    const bondFilter = naturalMotionConfig.skeletalBondFilter
      ? (neighborId: string) => naturalMotionConfig.skeletalBondFilter!(workspace, atom.id, neighborId)
      : undefined;
    const constraintType = getConstraintType(workspace, atom.id, bondFilter);

    // Handle orbit constraint (single-bond atoms) unless disabled
    if (constraintType === 'orbit' && !naturalMotionConfig.disableOrbitConstraint) {
      // Resolve orbit anchor using the same bond filter
      const bonds = getBondsForAtom(workspace, atom.id, true);
      const neighbor = bondFilter
        ? bonds.find(b => bondFilter(b.neighborId))
        : bonds.length === 1 ? bonds[0] : undefined;

      if (neighbor) {
        const dx = atom.x - neighbor.neighborX;
        const dy = atom.y - neighbor.neighborY;
        const radius = Math.sqrt(dx * dx + dy * dy);

        constraintRef.current = {
          type: 'orbit',
          anchorX: neighbor.neighborX,
          anchorY: neighbor.neighborY,
          radius,
        };
      }
    } else if (constraintType === 'rigid-body' || (constraintType === 'orbit' && naturalMotionConfig.disableOrbitConstraint)) {
      // Natural motion for multi-bond atoms, OR single-bond atoms when orbit is disabled
      // NATURAL MOTION: Calculate centroid-based offsets for blended translation/rotation
      // Use visibleOnly so hidden hydrogens don't skew centroid or bounding radius
      const connectedAtoms = getConnectedAtoms(workspace, atom.id, true);

      // Calculate fragment centroid (center of mass)
      const centroid = calculateFragmentCentroid(connectedAtoms);

      // Calculate bounding radius for blend factor normalization
      const boundingRadius = calculateBoundingRadius(connectedAtoms, centroid);

      // Current atom position is the initial pointer position (in SVG coords)
      const initialPointer: Point2D = { x: atom.x, y: atom.y };

      // Distance from centroid to where user grabbed
      const pointerDistanceFromCentroid = distance(centroid, initialPointer);

      // Basis angle: direction from centroid to initial pointer
      const basisAngle = angleFromTo(centroid, initialPointer);

      // Calculate blend factor (0 = translation, 1 = rotation)
      // Uses quadratic curve with deadzone from config
      const blendFactor = naturalMotionConfig.enableRotation
        ? calculateBlendFactor(
            pointerDistanceFromCentroid,
            boundingRadius,
            naturalMotionConfig.rotationDeadzone
          )
        : 0; // If rotation disabled, pure translation

      // Calculate offsets FROM CENTROID (not from dragged atom)
      // This enables rotation around the center of mass
      const atomOffsets = connectedAtoms.map((a) => ({
        id: a.id,
        offsetX: a.x - centroid.x,
        offsetY: a.y - centroid.y,
      }));

      const basis: NaturalMotionBasis = {
        centroid,
        boundingRadius,
        initialPointer,
        pointerDistanceFromCentroid,
        basisAngle,
        blendFactor,
        atomOffsets,
        draggedAtomId: atom.id,
      };

      constraintRef.current = {
        type: 'natural-motion',
        basis,
      };
    } else {
      constraintRef.current = { type: 'none' };
    }

    onDragStart();
  }, [motionValues, atom.x, atom.y, atom.id, workspace, onDragStart, naturalMotionConfig, anchorOrbitConfig, getAnchorData, getTranslationGroup, motionContext]);

  const handleDrag = useCallback(
    (_event: PointerEvent, info: PanInfo) => {
      if (!motionValues) return;

      // Capture starting position on first drag event if not set yet
      // (onDrag can fire before onDragStart)
      // IMPORTANT: Read from atom prop, not MotionValues, because Motion has already
      // modified the MotionValues by the time this fires
      if (!dragStartPos.current) {
        dragStartPos.current = {
          x: atom.x,
          y: atom.y,
        };
      }

      // Get the SVG element to access its CTM
      const svg = gRef.current?.ownerSVGElement;
      if (!svg) return;

      const ctm = svg.getScreenCTM();
      if (!ctm) return;

      // Convert screen pixel offset to SVG coordinate offset
      const scale = 1 / ctm.a;
      const svgDeltaX = info.offset.x * scale;
      const svgDeltaY = info.offset.y * scale;

      // Calculate current pointer position in SVG coordinates
      const currentPointer: Point2D = {
        x: dragStartPos.current.x + svgDeltaX,
        y: dragStartPos.current.y + svgDeltaY,
      };

      // FULL TRANSLATION: Pure translation for all atoms when all are selected
      if (constraintRef.current?.type === 'full-translation') {
        const { initialPositions } = constraintRef.current;

        // Calculate translation delta from drag start
        const deltaX = svgDeltaX;
        const deltaY = svgDeltaY;

        // Apply translation to all atoms in the group
        for (const initialPos of initialPositions) {
          const newX = initialPos.x + deltaX;
          const newY = initialPos.y + deltaY;

          if (initialPos.id === atom.id) {
            // Dragged atom: set MotionValue directly for immediate feedback
            motionValues.x.set(newX);
            motionValues.y.set(newY);
          } else {
            // Other atoms: set MotionValue directly for rigid body translation
            const atomMotion = motionContext.getAtomMotion(initialPos.id);
            if (atomMotion) {
              atomMotion.x.set(newX);
              atomMotion.y.set(newY);
            }
          }
        }
      } else if (constraintRef.current?.type === 'anchor-orbit') {
        // ANCHOR ORBIT: Pure rotation around selected anchor atom
        const { basis } = constraintRef.current;

        // Calculate angle change from initial pointer position
        const currentAngle = angleFromTo(basis.anchorPosition, currentPointer);
        const angleDelta = normalizeAngle(currentAngle - basis.initialPointerAngle);

        // Apply rotation to all orbiting atoms
        const newPositions = applyAnchorOrbitRotation(basis, angleDelta);

        // Update each orbiting atom's position
        for (const pos of newPositions) {
          if (pos.id === atom.id) {
            // Dragged atom: set MotionValue directly for immediate feedback
            motionValues.x.set(pos.x);
            motionValues.y.set(pos.y);
          } else {
            // Other orbiting atoms: set MotionValue directly for rigid rotation
            const atomMotion = motionContext.getAtomMotion(pos.id);
            if (atomMotion) {
              atomMotion.x.set(pos.x);
              atomMotion.y.set(pos.y);
            }
          }
        }
      } else if (constraintRef.current?.type === 'natural-motion') {
        // NATURAL MOTION: Blended translation/rotation for multi-bond fragments
        const { basis } = constraintRef.current;

        // Calculate rotation angle from basis
        // Current angle from centroid to pointer
        const currentAngle = angleFromTo(basis.centroid, currentPointer);

        // Delta angle from initial basis angle, normalized to [-PI, PI]
        const deltaAngle = normalizeAngle(currentAngle - basis.basisAngle);

        // Apply blend factor to rotation (0 = no rotation, 1 = full rotation)
        const rotationAngle = deltaAngle * basis.blendFactor;

        // Calculate translation (scaled by inverse of blend factor)
        // More rotation = less translation, and vice versa
        const translationScale = 1 - basis.blendFactor;
        const translationX = (currentPointer.x - basis.initialPointer.x) * translationScale;
        const translationY = (currentPointer.y - basis.initialPointer.y) * translationScale;

        // New centroid position after translation
        const newCentroid: Point2D = {
          x: basis.centroid.x + translationX,
          y: basis.centroid.y + translationY,
        };

        // Update each atom: translate centroid, then rotate around new centroid
        for (const atomOffset of basis.atomOffsets) {
          // Position relative to new centroid (before rotation)
          const unrotatedPos: Point2D = {
            x: newCentroid.x + atomOffset.offsetX,
            y: newCentroid.y + atomOffset.offsetY,
          };

          // Apply rotation around new centroid
          const finalPos = rotatePoint(unrotatedPos, newCentroid, rotationAngle);

          if (atomOffset.id === atom.id) {
            // Dragged atom: set MotionValue directly for immediate feedback
            motionValues.x.set(finalPos.x);
            motionValues.y.set(finalPos.y);
          } else {
            // Connected atoms: set MotionValue directly for rigid transform
            const atomMotion = motionContext.getAtomMotion(atomOffset.id);
            if (atomMotion) {
              atomMotion.x.set(finalPos.x);
              atomMotion.y.set(finalPos.y);
            }
          }
        }
      } else {
        // Original behavior for 'none' and 'orbit' constraints
        // (orbit is applied at drag end, not during drag)
        motionValues.x.set(currentPointer.x);
        motionValues.y.set(currentPointer.y);
      }
    },
    [motionValues, atom.x, atom.y, atom.id, motionContext]
  );

  const handleDragEnd = useCallback(() => {
    // Set cooldown if this was a rotation drag
    if (isDraggingRotation) {
      setRecentlyEndedDrag(true);
      if (dragCooldownRef.current) clearTimeout(dragCooldownRef.current);
      dragCooldownRef.current = setTimeout(() => {
        setRecentlyEndedDrag(false);
      }, 200); // 200ms cooldown to prevent cursor flashing

      // Notify parent callback if provided
      onRotationDragEnd?.();
    }

    // Reset rotation dragging state
    setIsDraggingRotation(false);

    // Calculate final positions for atoms that need settling
    let atomsToSettle: Array<{ id: string; x: number; y: number }> | undefined;

    if (motionValues) {
      const currentX = motionValues.x.get();
      const currentY = motionValues.y.get();

      if (constraintRef.current?.type === 'full-translation') {
        // FULL TRANSLATION: Settle all atoms at their current positions
        const { atomIds } = constraintRef.current;

        // Settle all atoms (including dragged atom) to their current motion values
        atomsToSettle = atomIds.map((atomId) => {
          const atomMotion = motionContext.getAtomMotion(atomId);
          if (atomMotion) {
            return {
              id: atomId,
              x: atomMotion.x.get(),
              y: atomMotion.y.get(),
            };
          }
          // Fallback: return current dragged atom position
          return { id: atomId, x: currentX, y: currentY };
        });
      } else if (constraintRef.current?.type === 'anchor-orbit') {
        // ANCHOR ORBIT: Settle all orbiting atoms at their current positions
        const { basis } = constraintRef.current;

        // Settle all orbiting atoms (including dragged atom) to their current motion values
        atomsToSettle = basis.orbitingAtoms.map((orbitAtom) => {
          const atomMotion = motionContext.getAtomMotion(orbitAtom.id);
          if (atomMotion) {
            return {
              id: orbitAtom.id,
              x: atomMotion.x.get(),
              y: atomMotion.y.get(),
            };
          }
          // Fallback: calculate position from basis
          const angle = orbitAtom.initialAngle;
          return {
            id: orbitAtom.id,
            x: basis.anchorPosition.x + orbitAtom.radius * Math.cos(angle),
            y: basis.anchorPosition.y + orbitAtom.radius * Math.sin(angle),
          };
        });
      } else if (constraintRef.current?.type === 'orbit') {
        // Calculate final position on orbit for the dragged atom itself
        const constrained = applyOrbitConstraint(
          currentX,
          currentY,
          constraintRef.current.anchorX!,
          constraintRef.current.anchorY!,
          constraintRef.current.radius!
        );

        atomsToSettle = [
          { id: atom.id, x: constrained.x, y: constrained.y }
        ];
      } else if (constraintRef.current?.type === 'natural-motion') {
        // NATURAL MOTION: Calculate final positions for connected atoms (excluding dragged atom)
        // The dragged atom's position is handled by Framer Motion's internal tracking
        const { basis } = constraintRef.current;

        // Settle all atoms EXCEPT the dragged one (matches old rigid-body behavior)
        atomsToSettle = basis.atomOffsets
          .filter((atomOffset) => atomOffset.id !== atom.id)
          .map((atomOffset) => {
            const atomMotion = motionContext.getAtomMotion(atomOffset.id);
            if (atomMotion) {
              return {
                id: atomOffset.id,
                x: atomMotion.x.get(),
                y: atomMotion.y.get(),
              };
            }
            // Fallback to original position
            return {
              id: atomOffset.id,
              x: basis.centroid.x + atomOffset.offsetX,
              y: basis.centroid.y + atomOffset.offsetY,
            };
          });
      }
    }

    // Reset for next drag
    dragStartPos.current = null;
    constraintRef.current = null;

    // Pass atoms to settle to parent
    onDragEnd(atomsToSettle);
  }, [atom.id, onDragEnd, motionValues, motionContext, isDraggingRotation, onRotationDragEnd]);

  // Pan-gesture wrappers. We drive dragging from framer-motion's pan gesture
  // (a pure gesture detector that applies no transform) rather than its drag
  // feature, so no internal `_dragX`/`_dragY` MotionValues leak onto the DOM
  // <g> (MC-937). Each wrapper honours the GestureManager gate set in
  // handlePointerDown before delegating to the existing drag logic.
  const handlePanStart = useCallback(() => {
    if (dragPreventedRef.current) return;
    setIsPanning(true);
    handleDragStart();
  }, [handleDragStart]);

  const handlePan = useCallback(
    (event: PointerEvent, info: PanInfo) => {
      if (dragPreventedRef.current) return;
      handleDrag(event, info);
    },
    [handleDrag]
  );

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
    if (dragPreventedRef.current) return;
    handleDragEnd();
  }, [handleDragEnd]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragCooldownRef.current) clearTimeout(dragCooldownRef.current);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent) => {
    // Consult GestureManager before starting drag — allows triggers
    // (e.g., bond creation from unselected atoms) to intercept
    if (selection?.gestureManager) {
      const result = selection.gestureManager.evaluateSync({
        gesture: 'drag-start',
        element: {
          type: 'atom',
          id: atom.id,
          selected: isSelected,
          hovered: isHovered,
        },
        position: { x: e.clientX, y: e.clientY },
        modifiers: {
          shift: e.shiftKey,
          ctrl: e.ctrlKey,
          alt: e.altKey,
          meta: e.metaKey,
        },
        selection: {
          count: selection.getSelectionCount?.() ?? 0,
          ids: Array.from(selection.getSelection?.() ?? []),
          types: { text: 0, atom: 0, bond: 0, lonepair: 0, charge: 0, arrow: 0, background: 0 },
        },
      });

      // If a trigger handled this and requested preventDefault, gate the pan
      // gesture so onPan*/handleDrag* no-op for this pointer sequence.
      if (result?.preventDefault) {
        dragPreventedRef.current = true;
        return;
      }
    }

    // Not intercepted — allow the pan gesture to drive dragging.
    dragPreventedRef.current = false;
  };
  

  // Visibility wrapper needed for both paths
  const isHidden = atom.visibility === 'hidden';

  if (!motionValues) {
    // No motion context - render static
    return (
      <g style={{ display: isHidden ? 'none' : undefined }}>
        <g transform={`translate(${atom.x}, ${atom.y})`}>
          {atom.graphics}
        </g>
      </g>
    );
  }

  // Wrap in visibility container - motion.g can't mix MotionValues with display CSS

  return (
    <g style={{ display: isHidden ? 'none' : undefined }}>
      <motion.g
        ref={gRef}
        style={{
          x: motionValues.x,
          y: motionValues.y,
          // Drag cursor while panning (replaces the old `whileDrag` cursor;
          // a plain string, so it never scrapes into a DOM attribute).
          cursor: isPanning ? (isDraggingRotation ? 'alias' : 'grabbing') : undefined,
        }}
        transition={springConfig.rigid ? { type: 'tween', duration: 0 } : springConfig}
        onPanStart={handlePanStart}
        onPan={handlePan}
        onPanEnd={handlePanEnd}
        onPointerDown={handlePointerDown}
        onMouseEnter={() => selection?.setHovered(atom.id)}
        onMouseLeave={() => selection?.setHovered(null)}
      >
        {/* Hit area for dragging - using FeedbackManager styling */}
        {/* Hide hitbox when hideAllHitboxes is true (e.g., during rotation
            drag) — UNLESS this atom is participating in the rotation:
            either the dragged atom itself ('anchor-orbit-dragging') or
            one of the atoms moving along with it ('anchor-orbit-orbiting',
            set by the host on each member of the rotation set). Both
            states have visible feedback styling, so the user sees the
            full rigid set that's about to move. */}
        {(!feedback.context?.hideAllHitboxes ||
          feedback.state === 'anchor-orbit-orbiting' ||
          feedback.state === 'anchor-orbit-dragging') && (
          <rect
          data-atom-id={atom.id}
            id={atom.id + "-hitbox"}
            x={-hitboxWidth / 2 + hydrogenOffset}
            y={-hitboxHeight / 2}
            rx={format.atom.radius}
            ry={format.atom.radius}
            width={hitboxWidth}
            height={hitboxHeight}
            fill={hitboxColor}
            fillOpacity={opacity}
            stroke={feedback.currentStyle.hitboxStroke}
            strokeWidth={feedback.currentStyle.atomStrokeWidth ?? 0}
            style={{ cursor: feedback.currentStyle.cursor ?? 'grab', pointerEvents: 'auto' }}
          />
        )}
      </motion.g>
    </g>
  );
});
